"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalInstallUpdateService = void 0;
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const ErrorHandler_1 = require("../Utils/ErrorHandler");
const WebRequestWorkerSingleton_1 = require("../Utils/WebRequestWorkerSingleton");
const DotnetCoreAcquisitionWorker_1 = require("./DotnetCoreAcquisitionWorker");
const IInstallManagementService_1 = require("./IInstallManagementService");
const InstallTrackerSingleton_1 = require("./InstallTrackerSingleton");
const StringConstants_1 = require("./StringConstants");
const versionUtils = require("./VersionUtilities");
/**
 * Manages local installs of .NET, by installing, uninstalling, and updating them sometime after vscode is launched.
 */
class LocalInstallUpdateService extends IInstallManagementService_1.IInstallManagementService {
    constructor(eventStream, extensionState, managementDirectoryProvider, installAction, uninstallAction, logger, installTrackerType = InstallTrackerSingleton_1.InstallTrackerSingleton) {
        super(eventStream);
        this.eventStream = eventStream;
        this.extensionState = extensionState;
        this.managementDirectoryProvider = managementDirectoryProvider;
        this.installAction = installAction;
        this.uninstallAction = uninstallAction;
        this.logger = logger;
        this.installTrackerType = installTrackerType;
    }
    ManageInstalls(updateCadenceMs = 300000) {
        return this.checkForUpdates(updateCadenceMs);
    }
    /**
     * Check for updates to the locally installed .NET runtimes.
     * The updates will not be installed immediately.
     * This will also uninstall any runtimes that are out of support or got updated and are not in use.
     *
     * @param workerContext
     * @param automaticUpdateDelayMs When should the updates be installed/uninstalled after launching VS Code? Default: 5 Minutes.
     * Check update 5 minutes after user starts VS Code, based on average time to intellisense + user activity to avoid disruption.
     */
    checkForUpdates() {
        return __awaiter(this, arguments, void 0, function* (automaticUpdateDelayMs = 300000) {
            const lastUpdateDate = this.extensionState.get('dotnet.latestUpdateDate', new Date(0));
            // Check if at least 1 day (24 hours) has passed since last update per SDL
            const oneDayMs = 24 * 60 * 60 * 1000;
            const timeOfLastUpdateMs = new Date(lastUpdateDate).getTime();
            const currentTimeMs = Date.now();
            const timeGapMs = (currentTimeMs - timeOfLastUpdateMs);
            this.eventStream.post(new EventStreamEvents_1.AutomaticUpdateCheck(`Checking for updates after ${automaticUpdateDelayMs}. Last Update Date: ${new Date(timeOfLastUpdateMs).toISOString()}. Current Date: ${new Date(currentTimeMs).toISOString()}`));
            this.logger.dispose();
            if (automaticUpdateDelayMs === 0 || timeGapMs >= oneDayMs) {
                this.eventStream.post(new EventStreamEvents_1.AutomaticUpdateCheck(`Commencing updates at ${Date.now().toString()} in ${automaticUpdateDelayMs} ms.`));
                this.logger.dispose();
                yield new Promise(resolve => setTimeout(resolve, automaticUpdateDelayMs));
                return this.automaticUpdate();
            }
            else {
                this.eventStream.post(new EventStreamEvents_1.AutomaticUpdateCheck(`Skipping update check as the last successful update was within 24 hours: ${timeGapMs} ms vs ${oneDayMs}`));
                this.logger.dispose();
                return Promise.resolve();
            }
        });
    }
    getInstallGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            const runtimeInstalls = (yield this.installTrackerType.getInstance(this.eventStream, this.extensionState).getExistingInstalls(this.managementDirectoryProvider, false)).filter(i => i.dotnetInstall.installMode !== 'sdk' && i.dotnetInstall.isGlobal !== true);
            const installGroupsToInstalls = new Map();
            for (const install of runtimeInstalls) {
                const majorMinor = versionUtils.getMajorMinorFromValidVersion(install.dotnetInstall.version);
                const architecture = install.dotnetInstall.architecture || DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture();
                const mode = install.dotnetInstall.installMode;
                if (majorMinor !== StringConstants_1.BAD_VERSION) // We never expect this to happen unless someone manually edited the data
                 {
                    const mapKey = `${mode}|${architecture}|${majorMinor}`;
                    if (!installGroupsToInstalls.has(mapKey)) {
                        installGroupsToInstalls.set(mapKey, { key: { mode, architecture, majorMinor }, installs: [install] });
                    }
                    else {
                        installGroupsToInstalls.get(mapKey).installs.push(install);
                    }
                }
            }
            return new Map(Array.from(installGroupsToInstalls.values()).map(groupInfo => [groupInfo.key, groupInfo.installs]));
        });
    }
    getAllNonUserOwnersOfCollection(installs) {
        const ownerSet = new Set();
        for (const install of installs) {
            for (const owner of install.installingExtensions) {
                if (owner && owner !== 'user') {
                    ownerSet.add(owner);
                }
            }
        }
        return Array.from(ownerSet);
    }
    automaticUpdate() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const isOffline = !(yield WebRequestWorkerSingleton_1.WebRequestWorkerSingleton.getInstance().isOnline(500, this.eventStream));
            if (isOffline) {
                return Promise.resolve();
            }
            const installGroupEntries = Array.from((yield this.getInstallGroups()).entries());
            let processedGroup = false;
            this.eventStream.post(new EventStreamEvents_1.UpdatingInstallGroups(`Automatic update triggered and detected the following install groups: ${JSON.stringify(installGroupEntries)}`));
            for (const [group, installsInGroup] of installGroupEntries) {
                const acquireContext = {
                    version: group.majorMinor,
                    architecture: group.architecture,
                    mode: group.mode,
                    installType: 'local',
                    requestingExtensionId: StringConstants_1.AUTOMATIC_UPDATE_EXTENSION_ID,
                    errorConfiguration: ErrorHandler_1.AcquireErrorConfiguration.DisableErrorPopups,
                    forceUpdate: true
                };
                // Make sure latest version is acquired (aka update - this defers download of the release manifest away from initialization)
                const addedInstall = yield this.installAction(acquireContext, true);
                // If acquire fails, then the update will throw - we don't want to uninstall if we don't have a newer version to use next time.
                // Get latest install - the install returned by acquire may still be a user managed path (not owned by us) if we're offline.
                const tracker = this.installTrackerType.getInstance(this.eventStream, this.extensionState);
                const extensionManagedInstalls = yield tracker.getExistingInstalls(this.managementDirectoryProvider, false);
                const newInstallsInGroup = extensionManagedInstalls.filter(i => i.dotnetInstall.installMode === group.mode &&
                    (i.dotnetInstall.architecture || DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture()) === group.architecture &&
                    versionUtils.getMajorMinorFromValidVersion(i.dotnetInstall.version) === group.majorMinor);
                // All of the installs were uninstalled in the middle of this process.
                if (newInstallsInGroup.length === 0) {
                    continue;
                }
                // Sort installations by version (patch/feature band since major.minor is all the same) and find the one with the highest version
                let latestInstall = newInstallsInGroup[0];
                if (newInstallsInGroup.length > 1) {
                    for (const install of newInstallsInGroup) {
                        // For runtime versions like 8.0.19, we need the full patch number
                        // For SDK versions, this is handled differently as they have formats like 7.0.301
                        const parts = install.dotnetInstall.version.split('.');
                        const latestParts = latestInstall.dotnetInstall.version.split('.');
                        // Compare full third component if it exists
                        if (parts.length > 2 && latestParts.length > 2) {
                            const currentPatch = Number((_b = (_a = parts[2].split('-')) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : 0); // Remove any suffix like -rc
                            const latestPatch = Number((_d = (_c = latestParts[2].split('-')) === null || _c === void 0 ? void 0 : _c[0]) !== null && _d !== void 0 ? _d : 0);
                            if (currentPatch > latestPatch) {
                                latestInstall = install;
                            }
                        }
                    }
                }
                // Make it so all owners of all installs the group own the latest version - owning basically means they depend on it / requested it (now in the past for a different version)
                const owners = this.getAllNonUserOwnersOfCollection(installsInGroup); // a user owner represents that the user installed it themselves, which is not true with auto-update
                yield tracker.addOwners(latestInstall.dotnetInstall, owners, this.managementDirectoryProvider);
                // Uninstall all in the group that are not live dependents and not the latest one (which should also be live but no need to check)
                const outdatedInstalls = installsInGroup.filter(i => i.dotnetInstall.version !== latestInstall.dotnetInstall.version);
                this.eventStream.post(new EventStreamEvents_1.UninstallingOutdatedInstalls(`The installs ${JSON.stringify(outdatedInstalls)} are outdated - attempting to uninstall. The latest install was ${JSON.stringify(latestInstall)}`));
                for (const install of outdatedInstalls) {
                    const uninstallContext = {
                        version: install.dotnetInstall.version,
                        architecture: install.dotnetInstall.architecture,
                        mode: install.dotnetInstall.installMode,
                        requestingExtensionId: StringConstants_1.AUTOMATIC_UPDATE_EXTENSION_ID,
                        installType: 'local',
                        errorConfiguration: ErrorHandler_1.AcquireErrorConfiguration.DisableErrorPopups
                    };
                    this.uninstallAction(uninstallContext, false, true).catch((e) => { });
                }
                processedGroup = true;
            }
            if (processedGroup) {
                yield this.extensionState.update('dotnet.latestUpdateDate', Date.now());
            }
            this.logger.dispose();
            return Promise.resolve();
        });
    }
}
exports.LocalInstallUpdateService = LocalInstallUpdateService;
//# sourceMappingURL=LocalInstallUpdateService.js.map